import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  Paper,
  Divider,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  HelpOutline,
} from "@mui/icons-material";
import Swal from "sweetalert2";

const GOLD = "#D4AF37";

export default function IntendedLearners({ courseId }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [learningObjectives, setLearningObjectives] = useState([""]);
  const token = localStorage.getItem("jwttoken");
  const API_BASE_URL = "https://tdtlworld.com/dss-kaas-backend/";

  const fetchCourseDetails = useCallback(async () => {
    if (!courseId || !token) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}lms/courses/${courseId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const keyPoints =
          Array.isArray(data.course_key_points) && data.course_key_points.length > 0
            ? data.course_key_points
            : [""];
        setLearningObjectives(keyPoints);
      } else {
        console.error("Failed to fetch course details.");
      }
    } catch (error) {
      console.error("Error fetching course details:", error);
    } finally {
      setIsLoading(false);
    }
  }, [courseId, token]);

  useEffect(() => {
    fetchCourseDetails();
  }, [fetchCourseDetails]);

  const handleObjectiveChange = (index, value) => {
    const newObjectives = [...learningObjectives];
    newObjectives[index] = value;
    setLearningObjectives(newObjectives);
  };

  const handleAddObjective = () => {
    setLearningObjectives([...learningObjectives, ""]);
  };

  const handleDeleteObjective = (index) => {
    if (learningObjectives.length <= 1) {
      Swal.fire({
        icon: "info",
        title: "Cannot Delete",
        text: "You must have at least one learning objective.",
        confirmButtonColor: GOLD,
      });
      return;
    }
    const newObjectives = learningObjectives.filter((_, i) => i !== index);
    setLearningObjectives(newObjectives);
  };

  const updateCourseKeyPoints = async () => {
    setIsSaving(true);
    const filteredObjectives = learningObjectives.filter((obj) => obj.trim() !== "");

    try {
      const response = await fetch(
        `${API_BASE_URL}lms/courses/${courseId}/update-key-points/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ course_key_points: filteredObjectives }),
        }
      );

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Key points updated successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        const errorData = await response.json();
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: errorData.error || "Something went wrong.",
          confirmButtonColor: GOLD,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update key points. Please try again later.",
        confirmButtonColor: GOLD,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      <Typography variant="h4" fontWeight={900} gutterBottom>
        Intended Learners
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        The descriptions below will be displayed on your course landing page to help students decide if this course is right for them.
      </Typography>

      <Paper 
        variant="outlined" 
        sx={{ 
          p: 4, 
          borderRadius: 4, 
          bgcolor: alpha(GOLD, 0.02),
          borderStyle: "dashed",
          borderColor: alpha(GOLD, 0.3),
          mb: 4
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            What will students learn in your course?
          </Typography>
          <HelpOutline sx={{ fontSize: 18, color: "text.secondary" }} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Enter at least 4 learning objectives that learners can expect to achieve after completing your course.
        </Typography>

        <Stack spacing={2}>
          {learningObjectives.map((objective, index) => (
            <TextField
              key={index}
              fullWidth
              placeholder={`Example: ${index % 2 === 0 ? "Define key concepts in..." : "Apply a framework to..."}`}
              value={objective}
              onChange={(e) => handleObjectiveChange(index, e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "background.paper",
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={() => handleDeleteObjective(index)}
                      disabled={learningObjectives.length <= 1}
                      sx={{ color: "error.main" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ))}
        </Stack>

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddObjective}
          sx={{ 
            mt: 3, 
            fontWeight: 700, 
            color: GOLD,
            "&:hover": { bgcolor: alpha(GOLD, 0.05) }
          }}
        >
          Add more to your response
        </Button>
      </Paper>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={updateCourseKeyPoints}
          disabled={isSaving}
          sx={{
            py: 1.5,
            px: 4,
            borderRadius: "50px",
            fontWeight: 800,
            bgcolor: GOLD,
            "&:hover": { bgcolor: alpha(GOLD, 0.8) }
          }}
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
}
